import dayjs from 'dayjs';
import type { UserDto } from 'src/model';

export class SummaryError extends Error {
    constructor(message?: string) {
        super(message);
        this.name = 'SummaryError';
    }
};

export const debounce = <F extends (...args: Parameters<F>) => ReturnType<F>>(func: F, delay: number): ((...args: Parameters<F>) => void) => {
    let timeout: ReturnType<typeof setTimeout>;
    return (...args: Parameters<F>) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), delay);
    };
};

export const throttle = <F extends (...args: Parameters<F>) => ReturnType<F>>(func: F, timeFrame: number): ((...args: Parameters<F>) => void) => {
    let lastTime = 0;
    return (...args: Parameters<F>) => {
        const now = new Date().getTime();
        if (now - lastTime >= timeFrame) {
            func(...args);
            lastTime = now;
        }
    };
};

export const merge = (obj1: any, obj2: any) => {
    const result = { ...obj1 };

    for (const key in obj2) {
        if (Object.hasOwn(obj2, key)) {
            if (obj2[key] instanceof Object && obj1[key] instanceof Object) {
                result[key] = merge(obj1[key], obj2[key]);
            }
            else {
                result[key] = obj2[key];
            }
        }
    }

    return result;
};

export enum Agents {
    Chrome = 'Google Chrome',
    Edge = 'Microsoft Edge',
};

export const currentAgent = (navigator as any).userAgentData?.brands?.some((b: any) => b.brand === Agents.Chrome)
    ? Agents.Chrome : (navigator as any).userAgentData?.brands?.some((b: any) => b.brand === Agents.Edge)
        ? Agents.Edge : null;

export const isMobile = window.innerWidth < 768;

export const controlDigit = (num: number | string): number => {
    const sum = num.toString().split('').reduce((sum, digit) => sum + parseInt(digit), 0);
    return sum > 9 ? controlDigit(sum) : sum;
};

export const isAdmin = (user?: UserDto) => (user?.roles.indexOf('admin') ?? -1) >= 0;
export const isExpert = (user?: UserDto) => (user?.roles.indexOf('expert') ?? -1) >= 0;

export const saveAs = (blob: Blob, name: string) => downloadFileAs(URL.createObjectURL(blob), name);

export const downloadFileAs = (url: string, name: string) => {
    // Namespace is used to prevent conflict w/ Chrome Poper Blocker extension (Issue #561)
    const a = document.createElementNS('http://www.w3.org/1999/xhtml', 'a') as HTMLAnchorElement;
    name = name || 'download';

    a.download = name;
    a.rel = 'noopener'; // tabnabbing

    a.href = url;
    setTimeout(() => URL.revokeObjectURL(a.href), 4E4); // 40s
    setTimeout(() => a.dispatchEvent(new MouseEvent('click')), 0);
};

export const formatTimeSpan = (seconds: number) => dayjs.duration(seconds, 'seconds').format(seconds >= 3600 ? 'H:mm:ss' : 'mm:ss');
